import { User } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

const TOKEN_KEY = "directToken";

const useDirectAuth = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<User | undefined>(undefined);

  const clearDirectToken = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(TOKEN_KEY);
    }

    setToken(undefined);
    setUser(undefined);
  }, []);

  const validateAndSetToken = useCallback(
    (tokenToValidate: string) => {
      try {
        const payload = jwtDecode(tokenToValidate) as User | undefined;

        if (payload && payload.exp * 1000 < Date.now()) {
          console.error("[useDirectAuth] direct token has expired");
          clearDirectToken();
          return false;
        }

        if (typeof window !== "undefined") {
          sessionStorage.setItem(TOKEN_KEY, tokenToValidate);
        }

        setToken(tokenToValidate);
        setUser(payload);
        return true;
      } catch (e) {
        console.error("[useDirectAuth] token is invalid", e);
        clearDirectToken();
        return false;
      }
    },
    [clearDirectToken]
  );

  const setDirectToken = useCallback(
    (newToken: string | null) => {
      if (newToken) {
        validateAndSetToken(newToken);
      } else {
        clearDirectToken();
      }
    },
    [validateAndSetToken, clearDirectToken]
  );

  // Initialize from URL params or sessionStorage
  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const tokenParam = searchParams?.get("token");

    if (tokenParam) {
      const isValid = validateAndSetToken(tokenParam);

      if (isValid) {
        const params = new URLSearchParams(searchParams?.toString());
        params.delete("token");
        const newParams = params.toString();
        const newUrl = newParams ? `${pathname}?${newParams}` : pathname;
        router.replace(newUrl!);
      }
    } else if (typeof window !== "undefined") {
      const storedToken = sessionStorage.getItem(TOKEN_KEY);
      if (storedToken) {
        validateAndSetToken(storedToken);
      }
    }

    setIsLoading(false);
  }, [isLoading, pathname, router, searchParams, validateAndSetToken]);

  return {
    isLoading,
    token,
    user,
    setDirectToken,
    clearDirectToken,
  };
};

export default useDirectAuth;
